import {
    type CSSProperties,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Separator,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    BellIcon,
    PlusIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "Separator";

const USAGE_CODE = `<Button
    spreadBehaviour="stretch"
    onPress={handleSignIn}
    title="Sign in"
/>

<Separator
    isShowTitle={true}
/>

<Button
    spreadBehaviour="stretch"
    onPress={handleRegister}
    title="Create account"
    variant="outline"
    type="neutral"
/>`;

const COLORS = [
    "subtle",
    "default",
    "emphasized"
] as const;

const TOOLBAR_SEPARATOR_LENGTH = 20;

const LIST_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 12
};

const TOOLBAR_STYLE: CSSProperties = {
    alignItems: "center",
    display: "flex",
    gap: 8
};

const ROW_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("separatorPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("separatorPage.labelTitle"),
        id: "label"
    },
    {
        title: t("separatorPage.styleTitle"),
        id: "style"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SeparatorPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isTitleShown,
        setIsTitleShown
    ] = useState(true);

    const handleToggleTitle = () => setIsTitleShown(!isTitleShown);

    return <Article>
        <ComponentHeader
            lead={t("separatorPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("separatorPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("separatorPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("separatorPage.overviewCaption")}
                isStacked={true}
            >
                <Button
                    title={t("separatorPage.signIn")}
                    onPress={() => undefined}
                    spreadBehaviour="stretch"
                />
                <Separator
                    isShowTitle={isTitleShown}
                />
                <Button
                    title={t("separatorPage.register")}
                    onPress={() => undefined}
                    spreadBehaviour="stretch"
                    variant="outline"
                    type="neutral"
                />
                <Switch
                    title={t("separatorPage.showLabel")}
                    onPress={handleToggleTitle}
                    isChecked={isTitleShown}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("separatorPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("separatorPage.orientationText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <div
                    style={LIST_STYLE}
                >
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                    >
                        {t("sample.item1")}
                    </Text>
                    <Separator/>
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                    >
                        {t("sample.item2")}
                    </Text>
                    <Separator/>
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                    >
                        {t("sample.item3")}
                    </Text>
                </div>
            </Preview>
            <Preview>
                <div
                    aria-label={t("separatorPage.toolbarLabel")}
                    style={TOOLBAR_STYLE}
                    role="toolbar"
                >
                    <Button
                        accessibilityLabel={t("separatorPage.add")}
                        onPress={() => undefined}
                        variant="ghost"
                        icon={PlusIcon}
                        type="neutral"
                        size="small"
                    />
                    <Button
                        accessibilityLabel={t("sample.search")}
                        onPress={() => undefined}
                        icon={SearchIcon}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                    <Separator
                        length={TOOLBAR_SEPARATOR_LENGTH}
                        orientation="vertical"
                    />
                    <Button
                        accessibilityLabel={t("separatorPage.star")}
                        onPress={() => undefined}
                        icon={StarIcon}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        accessibilityLabel={t("separatorPage.notifications")}
                        onPress={() => undefined}
                        icon={BellIcon}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                </div>
            </Preview>
        </Section>
        <Section
            title={t("separatorPage.labelTitle")}
            id="label"
        >
            <Paragraph>
                {t("separatorPage.labelText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Separator
                    isShowTitle={true}
                />
                <Separator
                    title={t("separatorPage.continueWith")}
                />
            </Preview>
        </Section>
        <Section
            title={t("separatorPage.styleTitle")}
            id="style"
        >
            <Paragraph>
                {t("separatorPage.styleText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    COLORS.map((color) => <div
                        style={ROW_STYLE}
                        key={color}
                    >
                        <Text
                            variant="bodySmallSize"
                            color="low"
                        >
                            {`color="${color}"`}
                        </Text>
                        <Separator
                            color={color}
                        />
                    </div>)
                }
                <div
                    style={ROW_STYLE}
                >
                    <Text
                        variant="bodySmallSize"
                        color="low"
                    >
                        {"length=\"50%\""}
                    </Text>
                    <Separator
                        length="50%"
                    />
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("separatorPage.a11yRole"),
                    t("separatorPage.a11yOrientation"),
                    t("separatorPage.a11yLabel"),
                    t("separatorPage.a11yStatic"),
                    t("separatorPage.a11yDeprecated")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("separatorPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        description: t("separatorPage.propDirection"),
                        type: "\"horizontal\" | \"vertical\"",
                        name: "direction"
                    },
                    {
                        description: t("separatorPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("separatorPage.propIsShowTitle"),
                        name: "isShowTitle",
                        type: "boolean"
                    },
                    {
                        description: t("separatorPage.propLength"),
                        type: "DimensionValue",
                        name: "length"
                    },
                    {
                        description: t("separatorPage.propColor"),
                        type: "keyof BorderContentColors",
                        defaultValue: "\"subtle\"",
                        name: "color"
                    },
                    {
                        description: t("separatorPage.propCustomColor"),
                        name: "customColor",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("separatorPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("separatorPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("separatorPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SeparatorPage;
